/**
 * DeliveryConfirmationSheet - Full-screen delivery confirmation form
 *
 * Captures GPS location, received-by info, signature, photos, video, and notes.
 * All form logic lives in useDeliveryForm hook.
 */
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  X, CheckCircle, Loader2, Clock, PenLine
} from 'lucide-react';
import SignaturePad from './SignaturePad';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import AccessCodeDetail from './driver/AccessCodeDetail';
import { savedAccessAddressKey } from '@/utils/accessCodeContext';
import DeliveryFailureForm from './delivery/DeliveryFailureForm';
import DeliveryGpsStatus from './delivery/DeliveryGpsStatus';
import DeliveryMediaCapture from './delivery/DeliveryMediaCapture';
import SavedDeliveryMedia from './delivery/SavedDeliveryMedia';
import SavedPhotoRecoveryPanel from './delivery/SavedPhotoRecoveryPanel';
import { useDeliveryForm } from '@/hooks/useDeliveryForm';

const STATIC_QUICK_FILL_OPTIONS = ['Left at door', 'Left with reception'];

/** Build the quick-fill chip list for "Received by". The recipient's own name
 * sits in front because that's the overwhelmingly most common entry. We drop
 * it if the recipient name is missing so the chip doesn't say "Unknown".
 */
const buildQuickFillOptions = (recipientName) => {
  const name = (recipientName || '').trim();
  return name ? [name, ...STATIC_QUICK_FILL_OPTIONS] : STATIC_QUICK_FILL_OPTIONS;
};


const DeliveryConfirmationSheet = ({ isOpen, onClose, order, onDeliveryConfirmed, openingId, sessionBinding }) => {
  const form = useDeliveryForm({ isOpen, order, onDeliveryConfirmed, onClose, openingId, sessionBinding });
  const [signatureDialogOpen, setSignatureDialogOpen] = useState(false);

  // Reset fail form when sheet closes. The signature itself needs no
  // reset/restore handling here — its thumbnail on the page is driven
  // directly by form.signatureData, whether fresh or restored from a draft.
  useEffect(() => {
    if (!isOpen) {
      setSignatureDialogOpen(false);
    }
  }, [isOpen]);

  if (!isOpen || !form.isVisible) return null;
  if (form.photoRecovery?.isOpen) return (
    <div className="fixed inset-0 z-50 bg-white overflow-y-auto p-4" data-testid="saved-photo-recovery-sheet">
      <SavedPhotoRecoveryPanel state={form.photoRecovery.state} onRefresh={form.photoRecovery.refresh}
        onKeepBoth={form.photoRecovery.keepBoth} onSelect={form.photoRecovery.select}
        onDiscard={form.photoRecovery.discard} onDiscardTab={form.photoRecovery.discardTab}
        onCheckReceipt={form.photoRecovery.checkReceipt} onClose={form.photoRecovery.close} />
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 bg-white flex flex-col" data-testid="delivery-confirmation-sheet">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-stone-200 bg-white sticky top-0">
        <button
          onClick={form.handleClose}
          className="p-2 -ml-2 hover:bg-stone-100 rounded-lg"
          disabled={form.isSubmitting}
          data-testid="close-confirmation-sheet"
        >
          <X className="h-5 w-5 text-stone-600" />
        </button>
        <h1 className="font-semibold text-stone-900">{form.isEditing ? 'Edit Delivery' : 'Confirm Stop'}</h1>
        <div className="w-9" />
      </div>

      {!form.photoPreparation?.enabled && <p className="px-4 py-2 text-xs text-stone-500">Drafts are kept only in this running tab; refreshing or closing the app loses them.</p>}
      {form.photoPreparation?.enabled && (form.photoPreparation.message || form.photoPreparation.otherDraft || form.kindConflict) && <div className="px-4 py-2 text-sm space-y-2" role="status" data-testid="photo-preparation-status">
        <p>{form.photoPreparation.message}</p>
        {(form.photoPreparation.needsCopyReview || form.kindConflict) && <Button variant="outline" onClick={form.photoRecovery.open} disabled={form.isSubmitting}>Review saved copies</Button>}
        {form.photoPreparation.otherDraft && <Button onClick={form.handleChooseSavedOperation} disabled={form.isSubmitting} data-testid="choose-saved-photo-operation">Review saved {form.photoPreparation.otherDraft.kind}</Button>}
        {form.photoPreparation.needsReview && !form.photoPreparation.needsCopyReview && <Button onClick={form.photoPreparation.review}>Continue saved edits</Button>}
        {form.canDiscardPhotoDraft && form.acceptedFailure && <Button variant="outline" onClick={form.handleRequestPhotoDiscard} data-testid="discard-photo-draft">Delete saved failure copy</Button>}
        {form.photoPreparation.busy
          ? <Button onClick={form.photoPreparation.cancel}>Cancel preparation</Button>
          : form.photoPreparation.canRetry && <Button onClick={form.photoPreparation.retry}>{form.photoPreparation.retryLabel}</Button>}
      </div>}

      {(form.hasPendingEdits || form.needsRecovery || form.needsUncertaintyReview || form.kindConflict || form.outcomeMessage) && (
        <div className="p-4 bg-amber-50 text-sm text-amber-950 space-y-2" role="status" data-testid="delivery-work-review">
          {form.hasPendingEdits && !form.photoPreparation?.enabled && <>
            <p>You have unsaved edits still pending. Save your edits or cancel.</p>
            <div className="flex flex-wrap gap-2">
              <Button onClick={form.handleSubmit} disabled={form.isSubmitting || !form.isFormValid} data-testid="save-delivery-edits">Save Edits</Button>
              <Button variant="outline" onClick={form.handleCancelEdits} disabled={form.isSubmitting} data-testid="cancel-delivery-edits">Cancel</Button>
            </div>
          </>}
          {form.needsRecovery && <p>Retained work needs review before continuing.</p>}
          {form.kindConflict && <p>Retained work belongs to a different delivery operation. It has not been converted or discarded. Close this form and resolve the order status before continuing.</p>}
          {form.outcomeMessage && <p>{form.outcomeMessage}</p>}
          {form.needsRecovery && !form.kindConflict && <Button onClick={form.handleResume} disabled={form.isSubmitting} data-testid="resume-delivery-work">Resume retained work</Button>}
          {form.needsUncertaintyReview && !form.photoPreparation?.enabled && !form.needsRecovery && !form.kindConflict && <Button onClick={form.handleReviewUncertainty} disabled={form.isSubmitting} data-testid="review-delivery-outcome">I reviewed the delivery; enable manual retry</Button>}
          {form.canSaveSnapshot && !form.photoPreparation?.enabled && <Button onClick={form.handleShowSaveOffline} disabled={form.isSubmitting} data-testid="save-retained-delivery-work">Save the exact confirmation attempt to this device</Button>}
        </div>
      )}
      {form.canCheckPhotoReceipt && !form.checkingPhotoStatus && <div className="px-4 py-2 flex flex-wrap gap-2"><Button onClick={form.handleCheckPhotoReceipt} disabled={form.isSubmitting} data-testid="check-photo-receipt">Check again</Button><Button variant="outline" onClick={form.photoRecovery.open} disabled={form.isSubmitting}>View or delete saved copy</Button></div>}
      {form.canRetryFailure && <div className="px-4 py-2"><Button onClick={form.handleFail} data-testid="retry-failure-report">Retry exact saved failure report</Button></div>}

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto">
        <fieldset disabled={form.controlsDisabled} className="p-4 space-y-6 min-w-0">

          {/* Delivery Info */}
          <div className="bg-stone-50 rounded-xl p-4">
            <p className="text-xs text-stone-500 mb-1">Delivering to</p>
            <p className="font-semibold text-stone-900">{order?.recipient_name || 'Unknown'}</p>
            <p className="text-sm text-stone-600">{order?.recipient_street_address}</p>
            {order?.recipient_street_address_2 && (
              <p className="text-sm text-stone-600">{order.recipient_street_address_2}</p>
            )}
            {order?.recipient_city && (
              <p className="text-sm text-stone-500">
                {order.recipient_city}, {order.recipient_state} {order.recipient_zip_code}
              </p>
            )}
            {/* Phase 2 — saved access codes for this stop, if any. */}
            <AccessCodeDetail
              key={`${openingId}:${order?.id}:${sessionBinding?.revision}`}
              orderId={order?.id}
              openingId={openingId}
              addressKey={savedAccessAddressKey(order)}
              disabled={form.controlsDisabled}
            />
          </div>

          <DeliveryGpsStatus status={form.gpsStatus} error={form.gpsError} />
          {!form.isEditing && form.gpsStatus !== 'cleared' && <Button type="button" variant="outline" onClick={form.clearLocation} disabled={form.controlsDisabled}>Clear location</Button>}

          {/* Delivery Time */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-stone-700">Time Delivered</label>
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-stone-400" />
              <input
                type="time"
                disabled={form.isEditing}
                value={form.deliveryTime}
                onChange={(e) => form.setDeliveryTime(e.target.value)}
                className="flex-1 px-3 py-2 border border-stone-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                data-testid="delivery-time-input"
              />
              {!form.isEditing && form.deliveryTime && <Button type="button" variant="outline" onClick={() => form.setDeliveryTime('')} disabled={form.controlsDisabled}>Clear time</Button>}
            </div>
            <p className="text-xs text-stone-400">{form.isEditing ? 'Original delivery time. Amendments do not change this time.' : 'Auto-set to now. Adjust if confirming a past delivery.'}</p>
          </div>

          {/* Received By */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-stone-700">
              Received by <span className="text-red-500">*</span>
            </label>
            <Input
              value={form.receivedBy}
              onChange={(e) => form.setReceivedBy(e.target.value)}
              placeholder="Name of person who received"
              className="bg-white"
              data-testid="received-by-input"
            />
            <div className="flex flex-wrap gap-2">
              {buildQuickFillOptions(order?.recipient_name).map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => form.setReceivedBy(option)}
                  className="text-xs px-2 py-1 rounded-full bg-stone-100 text-stone-600 hover:bg-stone-200 transition-colors"
                  data-testid={`quickfill-${option}`}
                >
                  {option}
                </button>
              ))}
            </div>
          </div>

          {/* Relationship */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-stone-700">
              Relationship to recipient <span className="text-stone-400 text-xs">(optional)</span>
            </label>
            <Input
              value={form.receivedByRelationship}
              onChange={(e) => form.setReceivedByRelationship(e.target.value)}
              placeholder="e.g., husband, coworker, neighbor"
              className="bg-white"
              data-testid="received-by-relationship-input"
            />
          </div>

          {/* Signature — only for new deliveries. Captured in a popup, not
              inline, so the signer can never accidentally reach the
              Confirm Delivery button while signing. */}
          {!form.isEditing && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-stone-700">
                  Signature <span className="text-stone-400 text-xs">(optional)</span>
                </label>
                {form.signatureData && (
                  <button
                    type="button"
                    onClick={() => form.setSignatureData(null)}
                    className="text-xs text-stone-400 hover:text-stone-600 transition-colors"
                  >
                    Remove
                  </button>
                )}
              </div>
              {form.signatureData ? (
                <button
                  type="button"
                  onClick={() => setSignatureDialogOpen(true)}
                  className="w-full flex items-center gap-3 p-2 border border-stone-200 rounded-lg hover:border-stone-300 transition-colors"
                  data-testid="view-signature-btn"
                >
                  <img
                    src={form.signatureData}
                    alt="Signature"
                    className="h-12 border border-stone-200 rounded bg-white"
                  />
                  <span className="text-sm text-stone-600">Signature captured — tap to view</span>
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => setSignatureDialogOpen(true)}
                  className="w-full flex items-center gap-3 px-4 py-3 border-2 border-dashed border-stone-300 rounded-lg text-stone-500 hover:border-stone-400 hover:text-stone-600 transition-colors"
                  data-testid="add-signature-btn"
                >
                  <PenLine className="h-5 w-5" />
                  <span className="text-sm">Add signature</span>
                </button>
              )}

              <Dialog open={signatureDialogOpen && !form.controlsDisabled} onOpenChange={setSignatureDialogOpen}>
                <DialogContent
                  className="max-w-md"
                  aria-describedby={undefined}
                  onInteractOutside={(e) => e.preventDefault()}
                  onEscapeKeyDown={(e) => e.preventDefault()}
                >
                  <DialogHeader>
                    <DialogTitle>Customer Signature</DialogTitle>
                  </DialogHeader>
                  <SignaturePad
                    onSignatureChange={form.setSignatureData}
                    initialSignature={form.signatureData}
                    width={400}
                    height={200}
                  />
                  <Button
                    type="button"
                    onClick={() => setSignatureDialogOpen(false)}
                    className="w-full"
                    data-testid="signature-done-btn"
                  >
                    Done
                  </Button>
                </DialogContent>
              </Dialog>
            </div>
          )}

        </fieldset>

        {/* Keep saved proof beside uploads, outside disabled draft fieldsets so
            current-record review remains available after uncertain removal. */}
        {form.isEditing && <SavedDeliveryMedia media={form.savedMedia} />}

        <fieldset disabled={form.controlsDisabled} className="p-4 space-y-6 min-w-0">
          {form.isEditing && <h2 className="text-sm font-semibold" data-testid="new-delivery-uploads">New uploads — not yet saved</h2>}
          <DeliveryMediaCapture
            photos={form.photos}
            video={form.video}
            photoInputRef={form.photoInputRef}
            videoInputRef={form.videoInputRef}
            handlePhotoCapture={form.handlePhotoCapture}
            handleVideoCapture={form.handleVideoCapture}
            removePhoto={form.removePhoto}
            handleUseOriginalPhoto={form.handleUseOriginalPhoto}
            removeVideo={form.removeVideo}
          />

          {/* Notes */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-stone-700">Notes (optional)</label>
            <Textarea
              value={form.notes}
              onChange={(e) => form.setNotes(e.target.value)}
              placeholder="Additional notes..."
              rows={2}
              className="bg-white resize-none"
              data-testid="notes-input"
            />
          </div>

          {/* ── Failed Delivery Section (only for new deliveries, not edits) ── */}
          {!form.isEditing && (
            <DeliveryFailureForm
              reason={form.failureReason}
              details={form.failureDetails}
              setReason={form.setFailureReason}
              setDetails={form.setFailureDetails}
              submitting={form.isSubmitting}
              disabled={form.controlsDisabled}
              onSubmit={form.handleFail}
            />
          )}
        </fieldset>
      </div>

      {/* Submit Button */}
      <div className="p-4 border-t border-stone-200 bg-white">
        {form.isSubmitting && form.uploadProgress > 0 && form.uploadProgress < 100 && (
          <div className="mb-3">
            <div className="flex justify-between text-xs text-stone-500 mb-1">
              <span>Uploading...</span>
              <span>{form.uploadProgress}%</span>
            </div>
            <div className="h-2 bg-stone-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-green-500 transition-all duration-300"
                style={{ width: `${form.uploadProgress}%` }}
              />
            </div>
          </div>
        )}
        <Button
          onClick={form.handleSubmit}
          disabled={!form.isFormValid || form.isSubmitting}
          className="w-full h-12 bg-green-600 hover:bg-green-700 text-white text-base font-medium"
          data-testid="confirm-delivery-submit"
        >
          {form.isSubmitting ? (
            <>
              <Loader2 className="h-5 w-5 mr-2 animate-spin" />
              {form.checkingPhotoStatus ? 'Checking delivery status…' : form.uploadProgress > 0 ? 'Uploading...' : form.isEditing ? 'Updating...' : 'Confirming...'}
            </>
          ) : (
            <>
              <CheckCircle className="h-5 w-5 mr-2" />
              {form.deliveryAccepted ? 'Done' : form.isEditing ? 'Update Delivery' : 'Confirm Delivery'}
            </>
          )}
        </Button>
      </div>

      {/* A confirmation with no response has an unknown server outcome.
          Dismissing this save prompt (X / outside / Escape) is "No": the
          form is left exactly as it was, nothing saved. */}
      <Dialog
        open={form.offlinePromptOpen}
        onOpenChange={(open) => { if (!open) form.handleDismissOffline(); }}
      >
        <DialogContent className="max-w-sm" data-testid="offline-save-prompt">
          <DialogHeader>
            <DialogTitle>{form.photoPreparation?.enabled ? 'Work saved on this device' : 'Server outcome unknown'}</DialogTitle>
          </DialogHeader>
          {form.photoPreparation?.enabled ? <p className="text-sm text-stone-600">
            Your photos and details are saved. Reopen this order on this device to continue.
            Closing does not send or confirm the delivery.
          </p> : <p className="text-sm text-stone-600">
            No response was received, so the server may already have accepted this confirmation.
            Save a local copy on this device. The app cannot reopen its contents or resend this older format.
            Saving does not confirm delivery.
          </p>}
          <div className="flex flex-col gap-2 pt-2">
            <Button
              type="button"
              onClick={form.handleSaveOffline}
              disabled={form.isSubmitting}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="offline-save-confirm"
            >
              {form.photoPreparation?.enabled ? 'Close' : 'Save to my device'}
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={form.handleDismissOffline}
              disabled={form.isSubmitting}
              className="w-full"
              data-testid="offline-save-cancel"
            >
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={form.discardPhotoDraftOpen} onOpenChange={(open) => { if (!open) form.handleCancelPhotoDiscard(); }}>
        <DialogContent className="max-w-sm" data-testid="photo-discard-prompt">
          <DialogHeader><DialogTitle>{form.acceptedFailure ? 'Discard local copy of accepted failure?' : 'Discard unsent local draft?'}</DialogTitle></DialogHeader>
          <p>This removes this draft's text and photo copies from this device. It cannot be undone. Camera-roll originals, server photos and other saved drafts are unchanged.</p>
          <Button variant="destructive" onClick={form.handleDiscardPhotoDraft} disabled={form.isSubmitting} data-testid="confirm-photo-discard">Discard this draft</Button>
          <Button variant="outline" onClick={form.handleCancelPhotoDiscard} disabled={form.isSubmitting}>Keep draft</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DeliveryConfirmationSheet;
